import { DOCUMENT_ASSEMBLER_LIMITS } from '@commenda/api';
import {
  Button,
  formatBytesString,
  IconButton,
  Icons,
  InputErrorMessage,
} from '@commenda/kakashi';
import { useRef, useState } from 'react';

const DOCX_ACCEPT =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx';

interface DocumentAssemblerDocxFieldProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
}

export const DocumentAssemblerDocxField = ({
  file,
  onFileChange,
  disabled = false,
}: DocumentAssemblerDocxFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejection, setRejection] = useState<string | null>(null);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0] ?? null;
    // Always clear the input, so picking the same file again still fires onChange.
    event.target.value = '';

    if (!picked) return;

    if (picked.size === 0) {
      setRejection('That file is empty.');
      onFileChange(null);
      return;
    }

    if (picked.size > DOCUMENT_ASSEMBLER_LIMITS.maxTemplateFileBytes) {
      setRejection(
        `“${picked.name}” is ${formatBytesString(picked.size)}. A template file must be ${formatBytesString(DOCUMENT_ASSEMBLER_LIMITS.maxTemplateFileBytes)} or smaller.`
      );
      onFileChange(null);
      return;
    }

    if (!picked.name.toLowerCase().endsWith('.docx')) {
      setRejection(
        'A template must be a Word .docx file. Word’s older .doc format and PDFs cannot be read.'
      );
      onFileChange(null);
      return;
    }

    setRejection(null);
    onFileChange(picked);
  };

  return (
    <div className="flex flex-col gap-sm">
      {/* The accept attribute is advisory only — a PDF renamed .docx passes it, and the server's
          OOXML check is the real gate. It saves nothing in that case, so no version appears. */}
      <input
        ref={inputRef}
        type="file"
        accept={DOCX_ACCEPT}
        className="hidden"
        onChange={onInputChange}
      />

      {file ? (
        <div className="flex items-center justify-between gap-md rounded-lg border border-border-primary px-lg py-md">
          <div className="flex min-w-0 items-center gap-sm">
            <Icons.File06 className="size-4 shrink-0 text-fg-tertiary" />
            <span className="txt-small-regular truncate text-text-primary">
              {file.name}
            </span>
            <span className="txt-extra-small-regular shrink-0 text-text-tertiary">
              {formatBytesString(file.size)}
            </span>
          </div>
          <IconButton
            variant="destructive"
            size="sm"
            aria-label={`Remove ${file.name}`}
            icon={<Icons.XClose className="size-4" />}
            disabled={disabled}
            onClick={() => {
              setRejection(null);
              onFileChange(null);
            }}
          />
        </div>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={disabled}
          leftIcon={<Icons.Paperclip className="size-4" />}
          onClick={() => inputRef.current?.click()}
        >
          Choose a .docx file
        </Button>
      )}

      {rejection ? <InputErrorMessage message={rejection} /> : null}
    </div>
  );
};
